import { LinkOutlined } from "@ant-design/icons";
import { Alert, Input, Select, Typography } from "antd";

const { Link } = Typography;
import { useCCEntityOptional } from "components/CC/context/CCEntityContext.tsx";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { CCEntityType, getAvailableFlowTypesForForm } from "components/CC/types/entityConfig.ts";
import { StartFlowType } from "components/CC/types/enums.ts";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { TagSelect } from "components/UI/TagSelect/TagSelect.tsx";
import { TextArea } from "components/UI/TextArea/TextArea.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useUser } from "hooks/useUser.ts";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { z } from "zod";

const INCOMING_POSTBACK_VARIABLES = [
    { label: "eid", value: "eid" },
    { label: "external_id", value: "external_id" },
    { label: "type", value: "type" },
    { label: "result", value: "result" },
    { label: "action", value: "action" },
    { label: "value", value: "value" },
    { label: "commission", value: "commission" },
    { label: "commission_id", value: "commission_id" },
    { label: "price", value: "price" },
    { label: "advertiser_id", value: "advertiser_id" },
];

const startFormSchema = z.object({
    active: z.boolean(),
    title: z.string(),
    subtitle: z.string(),
    flow: z.string(),
});

export type StartFormData = z.infer<typeof startFormSchema>;

interface FormProps {
    onChange?: (data: StartFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
    /** Existing flow types from other start nodes (for entity rules) */
    existingFlowTypes?: StartFlowType[];
    /** Total number of start nodes in the diagram */
    totalStarts?: number;
}

export const StartForm = ({
    onChange,
    onRegisterValidator,
    formId,
    existingFlowTypes = [],
    totalStarts = 1,
}: FormProps) => {
    const { selectedNode } = useSelectedNode();
    const formData = selectedNode?.formData as StartFormData | undefined;
    return (
        <GeneralFormProvider<StartFormData>
            key={selectedNode?.id}
            id={formId}
            schema={startFormSchema}
            defaultValues={formData}
        >
            <FormContent
                onChange={onChange}
                onRegisterValidator={onRegisterValidator}
                existingFlowTypes={existingFlowTypes}
                totalStarts={totalStarts}
                initialFlow={formData?.flow as StartFlowType | undefined}
            />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    onChange?: (data: StartFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    existingFlowTypes: StartFlowType[];
    totalStarts: number;
    /** Initial flow value from defaultValues - used to ensure current flow is always available */
    initialFlow?: StartFlowType;
}

const FormContent = ({
    onChange,
    onRegisterValidator,
    existingFlowTypes,
    totalStarts,
    initialFlow,
}: FormContentProps) => {
    const { control, trigger } = useGeneralForm<StartFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");
    const ccEntity = useCCEntityOptional();
    const userUtils = useUser();

    // Watch current flow value, fallback to initialFlow for first render
    const watchedFlow = useWatch({ control, name: "flow" }) as StartFlowType | undefined;
    const currentFlow = watchedFlow || initialFlow;

    const isTrackingIn = ccEntity?.entityType === CCEntityType.TrackingIn;
    const isIntegration = ccEntity?.entityType === CCEntityType.Integration;
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const endpointUrl = useMemo(() => {
        if (!isTrackingIn || !ccEntity?.entityId) return null;
        const baseApiUrl = import.meta.env.VITE_API_URL;
        const slug = userUtils?.getActiveTenant()?.slug_name ?? "{TENANT_SLUG}";
        const base = `${baseApiUrl}/${slug}/postbacks/handle?postback_id=${ccEntity.entityId}`;
        const params = new URLSearchParams();
        selectedTags.forEach((tag) => {
            params.set(tag, "REPLACE_ME");
        });
        const queryString = params.toString();
        return `${base}${queryString ? `&${queryString}` : ""}`;
    }, [isTrackingIn, ccEntity?.entityId, userUtils, selectedTags]);

    // Get available flow options based on entity config
    const { options: availableFlows, disabled: flowDisabled } = useMemo(() => {
        if (!ccEntity) {
            // No entity context - allow all types
            return {
                options: ["post", "ping", "postVerify", "webhook", "instant"] as StartFlowType[],
                disabled: false,
            };
        }
        return getAvailableFlowTypesForForm(ccEntity.entityType, currentFlow, existingFlowTypes, totalStarts);
    }, [ccEntity, currentFlow, existingFlowTypes, totalStarts]);

    // Build flow options for Select with i18n labels
    const flowOptions = useMemo(() => {
        return availableFlows.map((flow) => ({
            value: flow,
            label: t(`enums.flow_types.${flow}`),
        }));
    }, [availableFlows, t]);

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<StartFormData> onChange={onChange} />
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.start.forms.settings")}
                </Typography.Title>
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem className="flex-1 !mb-0" control={control} name="title" label={t("nodes.start.forms.title")}>
                    <Input placeholder={t("nodes.start.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.start.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.start.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Flow dropdown — Integration only */}
            {isIntegration && (
                <FormItem className="!mb-0" control={control} name="flow" label={t("nodes.start.forms.flow")}>
                    <Select
                        placeholder={t("nodes.start.forms.flow_placeholder")}
                        options={flowOptions}
                        disabled={flowDisabled}
                        allowClear={!flowDisabled && flowOptions.length > 1}
                    />
                </FormItem>
            )}

            {/* Endpoint URL — TrackingIn only */}
            {endpointUrl && (
                <div className="mt-4">
                    <Typography.Text className="block mb-1">{t("nodes.start.forms.endpoint")}</Typography.Text>
                    <TextArea rows={3} appearance="readonly" readOnly value={endpointUrl} />
                    <div className="mt-2">
                        <TagSelect
                            options={INCOMING_POSTBACK_VARIABLES}
                            value={selectedTags}
                            onChange={setSelectedTags}
                        />
                    </div>
                    <Alert
                        className="mt-3"
                        type="info"
                        showIcon
                        icon={<LinkOutlined style={{ fontSize: 13 }} />}
                        message={
                            <span className="text-sm">
                                {t("nodes.start.forms.parameters_guide")}{" "}
                                <Link
                                    className="text-sm"
                                    href="https://paldock.com/knowledge-base/tracking-parameters/"
                                    target="_blank"
                                    strong
                                >
                                    {t("nodes.start.forms.parameters_guide_link")}
                                </Link>
                            </span>
                        }
                    />
                </div>
            )}
        </div>
    );
};
